import classNames from 'classnames';
import {
  useState, useMemo,
} from 'react';
import { ReactComponent as SvgArrowWhiteBg } from 'public/assets/icons/arrow-white-bg.svg';
import { useOffers } from 'api/resort/useOffers';
import { ITALodgingReq } from 'components/common/PlanYourTrip/types';
import { SortByAccommodations, SortByAccommodationsDefault } from 'components/common/SortAccomodations/constants';
import { DateTime } from 'luxon';
import { accommodationSortingBySortType, sortAccommodationsByPrice } from 'util/accommodationsSortings';
import { IAccommodationPage } from 'api/tripadvisor/types';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { RESORT_IDS_TO_HIDE_LODGING_FOR } from 'ots-constants';
import { hasWeSkiIntegration } from 'components/common/PlanYourTrip/WeSki/resolver';
import { getLocale } from 'i18n/locale';
import { ELocale } from 'i18n/localeEnum';
import { SwiperComponent, SwiperSlideItem, CustomNavigationProps } from 'components/blocks/SwiperComponent';
import { SectionHeader } from '../SectionHeader';
import styles from './styles.module.scss';
import { Accommodation } from '../Accomodation';

const swiperConfig = {
  320: {
    slidesPerView: 1,
  },
  480: {
    slidesPerView: 1.5,
  },
  1100: {
    slidesPerView: 3,
  },
};

interface Props {
  lodgingProfile?: { dangerouslySetInnerHTML: { __html: string; }; };
  accommodations: IAccommodationPage | null;
  isMappedToWeSki: boolean;
  resortId: string;
}

export const WhereToStaySection = ({
  lodgingProfile, accommodations, isMappedToWeSki, resortId,
}: Props) => {
  const { t } = useOTSTranslation('common');

  const now = (DateTime.now().startOf('day'));
  const initialFromDate = now.plus({ days: 14 });
  const initialToDate = initialFromDate.plus({ days: 3 });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formData, setFormData] = useState<ITALodgingReq>({
    checkIn: DateTime.fromJSDate(initialFromDate.toJSDate()).toFormat('yyyy-MM-dd'),
    checkOut: DateTime.fromJSDate(initialToDate.toJSDate()).toFormat('yyyy-MM-dd'),
    numRooms: 1,
    numAdults: 2,
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sortBy, setSortBy] = useState<SortByAccommodations>(SortByAccommodationsDefault);

  const locationList = useMemo(
    () => (accommodations?.data ?? []).map((a) => a.location_id).sort(),
    [accommodations],
  );
  const { offerMap, areOffersLoading } = useOffers(
    locationList,
    formData.checkIn,
    formData.checkOut,
    formData.numAdults,
    formData.numAdults,
  );

  const sorted = useMemo(() => {
    if (accommodationSortingBySortType[sortBy]) {
      return sortAccommodationsByPrice(accommodations?.data ?? [], offerMap, sortBy);
    }
    return (accommodations?.data ?? []);
  }, [(accommodations?.data ?? []), sortBy, offerMap]);

  const shouldHideLodging = (isMappedToWeSki) || RESORT_IDS_TO_HIDE_LODGING_FOR.includes(resortId);

  const slides: SwiperSlideItem[] = useMemo(() => sorted
    .filter((acc) => {
      const offers = offerMap[acc.location_id];
      return offers && offers.length > 0 && offers.some((o) => !!o.clickUrl);
    })
    .map((acc) => ({
      key: acc.location_id,
      content: (
        <Accommodation
          className={styles.accommodation}
          accommodation={acc}
          isFresh
          offers={offerMap[acc.location_id]}
          areOffersLoading={areOffersLoading}
          withViewDealSection={false}
        />
      ),
    })), [sorted, offerMap, areOffersLoading]);

  const renderCustomNavigation = useMemo(() => (
    ({
      handlePrev, handleNext, activeIndex, slidesCount,
    }: CustomNavigationProps) => (
      <>
        {activeIndex !== 0 && (
        <button
          type="button"
          className={classNames(styles.swiperNavButton, styles.swiperButtonPrev)}
          onClick={handlePrev}
        >
          <SvgArrowWhiteBg />
        </button>
        )}
        {activeIndex !== slidesCount - 1 && (
        <button
          type="button"
          className={classNames(styles.swiperNavButton, styles.swiperButtonNext)}
          onClick={handleNext}
        >
          <SvgArrowWhiteBg />
        </button>
        )}
      </>
    )
  ), []);

  // https://mountainnews.jira.com/browse/OWR-1948
  if (getLocale() === ELocale.IT_IT && hasWeSkiIntegration({ resortId })) {
    return null;
  }

  const hasNoSlides = slides.length === 0;
  const shouldApplyReducedMargin = shouldHideLodging || hasNoSlides;
  const shouldRenderSwiper = !shouldHideLodging && !hasNoSlides;

  return (
    <section id={styles.whereToStay} className={classNames([shouldApplyReducedMargin && styles.shouldHideLodging])}>
      <SectionHeader
        title={t('resort.overview.whereToStay')}
        description={lodgingProfile}
      />
      {shouldRenderSwiper && (
        <SwiperComponent
          slides={slides}
          breakpoints={swiperConfig}
          spaceBetween={0}
          wrapperClassName={styles.whereToStaySwiper}
          slideClassName={styles.height_auto}
          renderCustomNavigation={renderCustomNavigation}
        />
      )}
    </section>
  );
};
